import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { LockOpen, Settings2, Rocket, Cpu, ArrowRight } from "lucide-react";

/**
 * MTLI Controls & Software (WCS/WES platform).
 * variant="service": for service pages (Electrical & Automation).
 * variant="partnership": framed as MTLI's software partnership play.
 */
const pillars = [
  {
    icon: LockOpen,
    title: "Open Architecture or Black Box",
    body: "Your choice: an open environment your team owns and extends, or a fully managed black-box build. Full custom either way, always vendor-agnostic.",
  },
  {
    icon: Settings2,
    title: "WCS & WES Combined",
    body: "Unified control and execution in a single platform. One layer orchestrating AS/RS, conveyors, AMRs, and robot cells, eliminating complexity and cost.",
  },
  {
    icon: Rocket,
    title: "Rapid, Scalable Implementation",
    body: "Pre-built integrations and standardized interfaces for common warehouse equipment. Start with one line, scale across the network, and API with most software as required.",
  },
];

const SoftwareControls = ({ variant = "service" }: { variant?: "service" | "partnership" }) => {
  const isPartnership = variant === "partnership";
  return (
    <section className="bg-secondary text-secondary-foreground py-16">
      <div className="container mx-auto px-4 max-w-5xl">
        <div className="text-center mb-10">
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-primary mb-2">
            {isPartnership ? "The MTLI Software Partnership Play" : "MTLI Controls & Software"}
          </p>
          <h2 className="text-2xl md:text-3xl font-bold text-balance">
            MTLI Controls &amp; Software: Agnostic by Design
          </h2>
          <p className="mt-3 text-secondary-foreground/80 max-w-2xl mx-auto">
            {isPartnership
              ? "Partner with MTLI and plug your products into one agnostic software layer. Our platform integrates any warehouse system: AS/RS, conveyors, AMRs, and robot cells. Your hardware plus our software and delivery means faster wins for every customer."
              : "A hardware and software agnostic platform that integrates with any warehouse system: AS/RS, conveyors, AMRs, and robot cells. Break free from vendor lock-in and future-proof your operation."}
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-5 mb-8">
          {pillars.map((p) => (
            <div key={p.title} className="rounded-xl bg-white/5 border border-white/10 p-6">
              <div className="mb-3 inline-flex h-10 w-10 items-center justify-center rounded-lg bg-primary/15">
                <p.icon className="h-5 w-5 text-primary" />
              </div>
              <h3 className="font-bold mb-2">{p.title}</h3>
              <p className="text-sm text-secondary-foreground/75 leading-relaxed">{p.body}</p>
            </div>
          ))}
        </div>

        {/* Industrial controls depth */}
        <div className="rounded-xl bg-white/5 border border-white/10 p-6 md:p-8 mb-8">
          <div className="flex items-start gap-4">
            <div className="hidden sm:flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/15">
              <Cpu className="h-5 w-5 text-primary" />
            </div>
            <div>
              <h3 className="font-bold mb-2">Deep Industrial Controls Expertise</h3>
              <p className="text-sm text-secondary-foreground/75 leading-relaxed">
                At the core is deep expertise in PLCs, DCS, and industrial automation equipment.
                Whether your operation runs on Siemens, Rockwell, Schneider Electric, or other leading
                control platforms, our software connects, monitors, and orchestrates your entire
                automation landscape through robust, industry-standard protocols.
              </p>
            </div>
          </div>
        </div>

        <div className="text-center">
          {isPartnership ? (
            <Button asChild size="lg" variant="secondary" className="px-8">
              <a href="#top">
                Partner on Software With MTLI
                <ArrowRight className="ml-2 h-5 w-5" />
              </a>
            </Button>
          ) : (
            <Button asChild size="lg" className="px-8">
              <Link to="/book-a-call">
                Talk Software With a Specialist
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </Button>
          )}
        </div>
      </div>
    </section>
  );
};

export default SoftwareControls;
